import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { PV } from '@/constants/testIds';
import { EXTERNAL, BRAND } from '@/constants/brand';
import { ArrowUpRight, Mail, Shield, Instagram, Facebook, FileText } from 'lucide-react';
import { setLeadSource } from '@/lib/leadSource';

export const Footer = () => {
  const { t } = useLanguage();
  const year = new Date().getFullYear();
  const copyright = t.footer.copyright.replace('{year}', year);

  return (
    <footer
      data-testid={PV.footer}
      className="relative pt-20 pb-10 overflow-hidden border-t border-slate-200"
      style={{ background: 'linear-gradient(180deg,#FAFBFD 0%,#F0F6FB 100%)' }}
    >
      <div className="pv-glow-blob w-[600px] h-[400px] bg-pv-teal/12 top-[-100px] left-1/2 -translate-x-1/2" />
      <div className="pv-glow-blob w-[420px] h-[420px] bg-pv-orange/8 bottom-[-160px] right-[-120px]" />

      <div className="pv-container relative">
        {/* Claim — big mantra */}
        <div className="text-center mb-16">
          <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-pv-teal mb-4 font-bold">
            {BRAND.name.toUpperCase()} · MANIFESTO
          </p>
          <h3 className="font-heading font-bold tracking-normal text-[32px] sm:text-[48px] md:text-[60px] leading-[1.06] text-pv-ink">
            {t.footer.claim_a}
            <br />
            <span className="pv-text-gradient">{t.footer.claim_b}</span>
          </h3>
        </div>

        <div className="pv-divider-line mb-12" />

        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8">
          {/* Brand block */}
          <div className="col-span-2 md:col-span-5">
            <Link to="/" className="flex items-center gap-3 mb-5" aria-label="Prime Vision · Home">
              <img
                src="/assets/prime-vision-logo.png"
                alt="Prime Vision"
                className="h-9 md:h-10 w-auto object-contain"
                draggable={false}
              />
            </Link>
            <p className="text-slate-600 text-[14.5px] leading-relaxed max-w-md">
              {t.footer.tagline}
            </p>
            <a
              href={`mailto:${BRAND.email}`}
              className="mt-6 inline-flex items-center gap-2 text-pv-teal text-sm font-bold hover:text-pv-tealDeep transition-colors"
            >
              <Mail className="w-4 h-4" /> {BRAND.email}
            </a>

            {/* Social */}
            <div className="mt-5 flex items-center gap-2.5">
              <a
                href={EXTERNAL.INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pv-footer-instagram"
                aria-label="Instagram Prime Vision"
                className="group inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-slate-200 hover:border-pv-orange/40 shadow-[0_8px_20px_-12px_rgba(15,23,42,0.10)] transition-all"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white" style={{ background: 'linear-gradient(45deg,#F58529 0%,#DD2A7B 45%,#8134AF 80%,#515BD4 100%)' }}>
                  <Instagram className="w-3.5 h-3.5" />
                </span>
                <span className="text-[12.5px] font-bold text-slate-800 group-hover:text-pv-orange">@primevisionsoftware</span>
              </a>
              <a
                href={EXTERNAL.FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pv-footer-facebook"
                aria-label="Facebook Prime Vision"
                className="group inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-slate-200 hover:border-[#1877F2]/40 shadow-[0_8px_20px_-12px_rgba(15,23,42,0.10)] transition-all"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1877F2] text-white">
                  <Facebook className="w-3.5 h-3.5" fill="currentColor" />
                </span>
                <span className="text-[12.5px] font-bold text-slate-800 group-hover:text-[#1877F2]">Facebook</span>
              </a>
            </div>
          </div>

          {/* Product links */}
          <div className="md:col-span-3">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-pv-teal mb-4 font-bold">
              {t.footer.links_product}
            </div>
            <ul className="space-y-3 text-[14px] text-slate-700">
              <li>
                <a
                  href={EXTERNAL.AUTH_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={PV.footerLoginLink}
                  className="inline-flex items-center gap-1.5 hover:text-pv-teal transition-colors font-semibold"
                >{t.footer.access} <ArrowUpRight className="w-3 h-3" /></a>
              </li>
              <li>
                <Link
                  to="/bookacall"
                  data-testid={PV.footerDemoLink}
                  onClick={() => setLeadSource('footer-demo')}
                  className="hover:text-pv-teal transition-colors font-semibold"
                >{t.footer.demo}</Link>
              </li>
              <li>
                <a
                  href={EXTERNAL.SURVEY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 hover:text-pv-teal transition-colors font-semibold"
                >{t.footer.survey} <ArrowUpRight className="w-3 h-3" /></a>
              </li>
              <li>
                <Link to="/contatti" data-testid={PV.footerContactLink} onClick={() => setLeadSource('footer-contact')} className="hover:text-pv-teal transition-colors font-semibold">{t.footer.contact}</Link>
              </li>
              <li>
                <Link to="/faq" data-testid="pv-footer-faq-link" className="hover:text-pv-teal transition-colors font-semibold">FAQ</Link>
              </li>
              <li>
                <Link
                  to="/brochure?utm_source=site&utm_medium=footer&utm_campaign=brochure"
                  data-testid="pv-footer-brochure-link"
                  className="inline-flex items-center gap-1.5 hover:text-pv-teal transition-colors font-semibold"
                >
                  <FileText className="w-3 h-3" />
                  Brochure Prime Vision
                </Link>
              </li>
              <li>
                <Link
                  to="/fse?utm_source=site&utm_medium=footer&utm_campaign=fse"
                  data-testid="pv-footer-fse-link"
                  className="inline-flex items-center gap-1.5 hover:text-pv-teal transition-colors font-semibold"
                >
                  <FileText className="w-3 h-3 text-pv-orange" />
                  Guida FSE 2.0
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-pv-teal mb-4 font-bold">
              {t.footer.links_legal}
            </div>
            <ul className="space-y-3 text-[14px] text-slate-700">
              <li>
                <a
                  href={`${EXTERNAL.SITE_URL}/privacy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={PV.footerPrivacyLink}
                  className="hover:text-pv-teal transition-colors font-semibold"
                >{t.footer.privacy}</a>
              </li>
              <li>
                <a
                  href={`${EXTERNAL.SITE_URL}/termini`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-pv-teal transition-colors font-semibold"
                >{t.footer.terms}</a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('pv:open-cookie-settings'))}
                  data-testid="pv-footer-cookie-prefs"
                  className="text-left hover:text-pv-teal transition-colors font-semibold inline-flex items-center gap-1.5"
                >
                  <Shield className="w-3 h-3" />
                  Preferenze cookie
                </button>
              </li>
            </ul>

            <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4 shadow-[0_8px_24px_-16px_rgba(15,23,42,0.10)]">
              <div className="flex items-center gap-2 mb-1.5">
                <Shield className="w-3.5 h-3.5 text-pv-teal" />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-pv-teal font-bold">GDPR · ISO 27001</span>
              </div>
              <p className="text-[12.5px] text-slate-600 leading-snug">Dati su server europei. Crittografia AES-256 end-to-end.</p>
            </div>
          </div>
        </div>

        {/* Watermark */}
        <div className="relative mt-16 select-none pointer-events-none">
          <div
            className="text-[80px] sm:text-[140px] md:text-[200px] lg:text-[260px] font-heading font-bold leading-none tracking-normal text-center bg-clip-text text-transparent"
            style={{ backgroundImage: 'linear-gradient(180deg, rgba(27,127,184,0.16) 0%, rgba(27,127,184,0.02) 100%)' }}
          >
            PRIME VISION
          </div>
        </div>

        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-3 text-[12.5px] text-slate-500">
          <span>{copyright}</span>
          <span className="font-mono uppercase tracking-[0.22em] font-semibold">v 1.0 · made for ophthalmologists</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
